import { ObjectId } from 'bson';
import { useRouteMatch } from 'react-router';

import type { Account } from '@feathr/blackbox';

import { assertAuthenticatedStore, useStore } from '.';

class NoAccountError extends Error {
  constructor(message = 'No account context available.') {
    const trueProto = new.target.prototype;
    super(message);
    Object.setPrototypeOf(this, trueProto);
  }

  public override toString(): string {
    return `NoAccountError: ${this.message}`;
  }
}

export function assertAccount(account: Account | undefined): asserts account {
  // Only treat an errored account as "no context" when it was never fetched (a
  // failed load / 403). A failed mutation flags isErrored but leaves a fetched,
  // usable account in place — throwing there would crash every useAccount consumer.
  if (account === undefined || account.isEphemeral || (account.isErrored && !account.isFetched)) {
    throw new NoAccountError();
  }
}

interface IAccountOptions {
  required: boolean;
}

function useAccount(): Account;
function useAccount(options: { required: true }): Account;
function useAccount(options: { required: false }): Account | undefined;
function useAccount(options: IAccountOptions = { required: true }): Account | undefined {
  const store = useStore();
  assertAuthenticatedStore(store);

  let account: Account | undefined;
  const { required } = options;

  const match = useRouteMatch<{ accountId?: string }>({
    /*
     * Include trailing slash and strict=true to ensure matched path includes
     * accountId and at least one child path element.
     */
    path: '/:accountId',
    sensitive: true,
  });

  const { Accounts } = store;

  const pathAccountId = match && match.params.accountId;
  const pathAccountIdIsValid = pathAccountId && ObjectId.isValid(pathAccountId);

  const accountId: string | undefined =
    pathAccountId && pathAccountIdIsValid ? pathAccountId : undefined;
  if (accountId) {
    if (accountId !== sessionStorage.getItem('accountId')) {
      // If we change account context, set new account context in session storage
      sessionStorage.setItem('accountId', accountId);
    }
    account = Accounts.get(accountId);
  }

  if (required) {
    assertAccount(account);
  }

  return account;
}

export default useAccount;
export { NoAccountError };
